import React from 'react';

import {
  StatesButtonStates as BUTTON_STATES,
  StatesButton,
  Text,
  TextTypography as TYPOGRAPHY,
  Dialog as DialogComponent,
  Spinner,
  Button,
  ButtonPriority,
} from 'wix-ui-tpa';

import { useCalendarActions } from '../../Hooks/useCalendarActions';
import {
  DialogState,
  DialogViewModel,
} from '../../ViewModel/dialogViewModel/dialogViewModel';
import { DataHooks } from './constants';
import { classes, st } from './Dialog.st.css';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { WidgetElements } from '../../../../utils/bi/consts';

export type DialogProps = {
  viewModel: DialogViewModel;
};

const Dialog: React.FC<DialogProps> = ({ viewModel }) => {
  const { onDialogClose } = useCalendarActions();
  const { isMobile } = useEnvironment();
  const {
    contentText,
    titleText,
    isOpen,
    confirmButtonUrlLink,
    closeButtonText,
  } = viewModel;

  return (
    <DialogComponent
      data-hook={DataHooks.Root}
      isOpen={isOpen}
      onClose={() => onDialogClose()}
      className={st(classes.root, { isMobile })}
      notFullscreenOnMobile
    >
      {titleText && (
        <Text
          className={classes.title}
          data-hook={DataHooks.Title}
          typography={TYPOGRAPHY.largeTitle}
        >
          {titleText}
        </Text>
      )}
      <Text
        className={classes.content}
        data-hook={DataHooks.Content}
        typography={TYPOGRAPHY.listText}
      >
        {contentText}
      </Text>
      <div className={classes.buttonsContainer}>
        {confirmButtonUrlLink ? (
          <a
            href={confirmButtonUrlLink}
            target="_blank"
            data-hook={DataHooks.Link}
            rel="noreferrer"
          >
            <ConfirmButton viewModel={viewModel} />
          </a>
        ) : (
          <ConfirmButton viewModel={viewModel} />
        )}
        {closeButtonText ? <CloseButton text={closeButtonText} /> : null}
      </div>
    </DialogComponent>
  );
};

export default Dialog;

const ConfirmButton: React.FC<DialogProps> = ({ viewModel }) => {
  const { state, confirmButtonText } = viewModel;
  const { onDialogConfirm } = useCalendarActions();
  const buttonState: BUTTON_STATES =
    state === DialogState.IDLE ? BUTTON_STATES.IDLE : BUTTON_STATES.IN_PROGRESS;

  return (
    <StatesButton
      data-hook={DataHooks.ConfirmButton}
      className={classes.button}
      onClick={() => onDialogConfirm()}
      state={buttonState}
      idleContent={
        <Text className={classes.buttonText} typography={TYPOGRAPHY.listText}>
          {confirmButtonText}
        </Text>
      }
      inProgressContent={
        <Spinner className={classes.spinner} diameter={24} isCentered={true} />
      }
    />
  );
};

const CloseButton = ({ text }: { text: string }) => {
  const { onDialogClose } = useCalendarActions();
  const { isMobile } = useEnvironment();

  return (
    <Button
      data-hook={DataHooks.CloseButton}
      onClick={() => onDialogClose(WidgetElements.DIALOG_CLOSE_BUTTON)}
      priority={ButtonPriority.basicSecondary}
      className={classes.closeButton}
      fullWidth={isMobile}
    >
      {text}
    </Button>
  );
};
